<template>
  <div>
    <!-- Calendar controls -->
    <div class="row">
      <div class="col-sm-4" />
    </div>
    <div
      v-if="!showWizard"
      class="row col-sm-12 nopads"
    >
      <div class="col-md-4 mt-4">
        <div class="d-flex align-items-center">
          <button
            id="fa-arrow-date-left"
            class="btn btn-sm btn-outline-secondary"
            @click="addDays(+7)"
          >
            <i class="fa fa-arrow-left" />
          </button>
          <input
            v-model="calendarStartDate"
            class="form-control form-control-sm"
            type="date"
          >
          <button
            id="fa-arrow-date-right"
            class="btn btn-sm btn-outline-secondary"
            @click.stop="addDays(-7)"
          >
            <i class="fa fa-arrow-right" />
          </button>
          <button
            class="btn btn-sm btn-outline-secondary ms-2"
            @click.stop="goToToday"
          >
            {{ $t('common.today') }}
          </button>
        </div>
      </div>
      <div
        v-if="!disableContractActions"
        class="col-md-2 mt-4"
      >
        <div class="form-check">
          <input
            id="isInactive"
            v-model="onlyUserContracts"
            class="form-check-input"
            type="checkbox"
          >
          <label
            class="form-check-label own-check"
            for="isInactive"
          >
            {{ $t('contracts.only_user_owned_contracts') }}
          </label>
        </div>
      </div>
      <div
        id="filter"
        class="col-md-3"
      >
        <span style="font-size: .9em">{{ $t('calendar.order_status') }} </span>
        <multiselect
          v-model="value"
          class="col-12"
          :multiple="true"
          :options="orderStatusValues"
          :custom-label="value => orderStatusNames[value]"
          :searchable="true"
          :close-on-select="false"
          :placeholder="$t('orders.select_status')"
          :select-label="$t('orders.select_state')"
          :selected-label="$t('common.selected')"
          :deselect-label="$t('common.remove_selection')"
        />
      </div>
      <div
        id="filter"
        class="col-md-3"
      >
        <span style="font-size: .9em">{{ $t('calendar.assignment_status') }} </span>
        <multiselect
          v-model="assignmentsValue"
          :multiple="true"
          class="col-sm-12"
          :options="assignmentStatusValues"
          :custom-label="assignmentsValue => assignmentStatusNames[assignmentsValue]"
          :close-on-select="false"
          :searchable="false"
          :placeholder="$t('orders.select_status')"
          :select-label="$t('orders.select_state')"
          :selected-label="$t('common.selected')"
          :deselect-label="$t('common.remove_selection')"
        />
      </div>
      <div
        v-if="!disableContractActions"
        class="col-md-2 text-right my-4"
      >
        <button
          class="form-button btn btn-success"
          @click="addContract"
        >
          {{ $t('calendar.new_contract') }}
        </button>
      </div>
    </div>
    <div
      v-if="!showWizard"
      class="calendar-row mb-2"
      :class="{
        'mt-4': disableContractActions,
      }"
    >
      <!-- Calendar header -->
      <calendar-header
        :text-col-width-percentage="40"
        :time-col-width="timeColWidth"
        :visible-weeks-limit="visibleWeeksLimit"
        :calendar-start-week="calendarStartWeek"
      />

      <!-- Contracts -->
      <contract-calendar-item
        v-for="contractC in contracts"
        :key="contractC.id"
        ref="contracts"
        :contract="contractC"
        :calendar-start-week="calendarStartWeek"
        :calendar-start-date="new Date(calendarStartDate)"
        :visible-weeks-limit="visibleWeeksLimit"
        :time-col-width="timeColWidth"
        :text-col-width-percentage="40"
        :filter="value"
        :assignment-filter="assignmentsValue"
        :auto-expand="autoExpand"
        :disable-contract-selection="disableContractActions"
        @show-contract="showContract"
        @show-order="showOrder"
        @add-order="addOrder"
        @order-updated="$emit('orderUpdated')"
        @add-assignment="addAssignment"
        @show-assignment="showAssignment"
        @select-duty-officer-user="$emit('selectDutyOfficerUser', $event)"
        @duty-officer-updated="$emit('dutyOfficerUpdated')"
      />
    </div>

    <Transition name="fade">
      <contract-wizard
        v-if="showWizard"
        :user="user"
        @customer-set="setCustomer"
        @contractor-set="setContractor"
        @close-wizard="closeWizard"
      />
    </Transition>

    <base-modal
      v-if="(selectedContract && !showWizard) || (editMode && !showWizard)"
      size="xl"
      :hide-footer="true"
      :hide-header="true"
    >
      <contract-editor
        v-if="selectedContract"
        v-model="selectedContract"
        :contract-trip-list="true"
        :active-order-editor="activeOrderEditor"
        :user="user"
        :show-title="false"
        @close="closeAndUpdate(selectedContract)"
      />
    </base-modal>

    <base-modal
      v-if="selectedOrder"
      :hide-footer="true"
      :hide-header="true"
      size="lg"
    >
      <order-editor
        :order="selectedOrder"
        :contract="contract ? contract : clickedContract"
        :user="user"
        :show-title="false"
        @close="closeOrder"
        @close-and-update="closeOrder"
      />
    </base-modal>

    <base-modal
      v-if="selectedAssignment"
      :hide-footer="true"
      :hide-header="true"
      size="lg"
    >
      <work-assignment-editor
        :work-assignment="selectedAssignment"
        :is-observer="hasObserver(user.roles)"
        :user="user"
        :no-margin="true"
        @close="closeAssignmentEditor"
        @close-and-update="closeAssignment"
      />
    </base-modal>
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>


<script>
import {orderStatus, workManagementHelper} from '../mixins/WorkManagementMixin'
import {workAssignmentHelper} from '../mixins/WorkAssignmentMixin'
import {restApi} from '../mixins/RestApiMixin'
import CalendarHeader from "@/components/contractcalendar/CalendarHeader";
import ContractCalendarItem from "@/components/contractcalendar/ContractCalendarItem";
import {timeUtils} from "@/components/mixins/TimeUtils";
import OrderEditor from "@/components/order/OrderEditor";
import ContractWizard from "@/components/contract/ContractWizard";
import WorkAssignmentEditor from "@/components/workassignment/WorkAssignmentEditor";
import {userHelper} from "@/components/mixins/UserMixin";
import ContractEditor from "@/components/contract/ContractEditor";
import Navbar from "@/components/utils/Navbar.vue";
import BaseModal from "@/components/BaseModal.vue";

export default {
    name: 'ContractCalendar',
    components: {
        BaseModal,
        Navbar,
        ContractEditor,
        WorkAssignmentEditor, ContractWizard,
        OrderEditor, ContractCalendarItem, CalendarHeader},
    mixins: [workManagementHelper, workAssignmentHelper, restApi, timeUtils, userHelper],
    props: {
        contract: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            default: null
        },
        disableContractActions: {
            type: Boolean,
            default: false
        },
        autoExpand: {
            type: Boolean,
            default: false
        }
    },
    data: function () {
        return {
            visibleWeeksLimit: 20,
            calendarStartDate: this.getCurrentDay(),
            calendarStartWeek: this.getWeekNumber(this.getCurrentDay()),
            timeColWidth: 50,
            contracts: [],
            orders: [],
            selectedContract: null,
            showWizard: false,
            selectedOrder: null,
            selectedAssignment: null,
            activeOrderEditor: null,
            onlyUserContracts: true,
            editMode: false,
            loading: false,
            filter: [],
            value: [],
            assignmentFilter: [],
            assignmentsValue: [],
            clickedContract: null
        }
    },
    computed: {
        orderStatusNames() {
            return {
                [orderStatus.STORED]: this.$t('orders.status_stored'),
                [orderStatus.PLAN]: this.$t('orders.status_plan'),
                [orderStatus.OPEN]: this.$t('orders.status_open'),
                [orderStatus.IN_PROGRESS]: this.$t('orders.status_in_progress'),
                [orderStatus.COMPLETED]: this.$t('orders.status_completed'),
                [orderStatus.CANCELLED]: this.$t('orders.status_cancelled'),
                [orderStatus.ABORTED]: this.$t('orders.status_aborted'),
                [orderStatus.APPROVED]: this.$t('orders.status_approved'),
                [orderStatus.INVOICED]: this.$t('orders.status_invoiced'),
                [orderStatus.CLOSED]: this.$t('orders.status_closed'),
            }
        },
        orderStatusValues() {
            return Object.values(orderStatus)
        },
        assignmentStatusNames() {
            return {
                [this.OPEN]: this.$t('orders.status_open'),
                [this.COMPLETED]: this.$t('orders.status_completed'),
                [this.ASSIGNED]: this.$t('work_assignment.assigned'),
                [this.IN_PROGRESS]: this.$t('orders.status_approved'),
                [this.CANCELLED]: this.$t('orders.status_cancelled'),
            }
        },
        assignmentStatusValues() {
            return [this.OPEN, this.ASSIGNED, this.IN_PROGRESS, this.COMPLETED, this.CANCELLED]
        }
    },

    watch: {
        calendarStartDate() {
            this.calendarStartWeek = this.getWeekNumber(this.calendarStartDate)
        },
        onlyUserContracts() {
            this.initSearchParams()
            this.fetchContracts(this.lastSearchedParams)
        },
    },
    mounted() {
        if (this.contract) {
            //If start_date is null use current day as fallback to keep calendar working
            if (this.contract.start_date) {
                this.calendarStartDate = this.timestampToDateInput(this.contract.start_date)
            }
            this.contract.start_date = this.timestampToISOString(this.contract.start_date)
            this.contract.end_date = this.timestampToISOString(this.contract.end_date)
            this.contracts = [this.contract]
        } else {
            this.initSearchParams()
            this.fetchContracts(this.lastSearchedParams)
        }
        // Pre-select order values to ongoing or open ones only
        this.value=[this.OPEN, this.IN_PROGRESS]
    },
    methods: {

        initSearchParams() {
            let now = this.calendarStartDate;
            let to = new Date()
            to.setDate(to.getDate() + (7 * this.visibleWeeksLimit));
            this.lastSearchedParams = {
                startDate: now,
                endDate: to.toISOString(),
                onlyUserOwnedContracts: this.onlyUserContracts ? 1 : 0
            }
        },

        fetchContracts: function (params) {
            this.loading = true
            this.lastSearchedParams = params
            this.contracts = []
            this.restFetchParams(this.contractOpenUrl, this.lastSearchedParams, this.handleResponse)
        },

        handleResponse: function (response) {
            this.loading = false
            if (response && response.data) {
                this.contracts = response.data
            }
        },
        closeAndUpdate(updatedContract) {
            let index = this.contracts.findIndex(item => item.id === updatedContract.id)
            if (index >= 0) {
                this.$refs.contracts[index].refreshContract(updatedContract)
            }
            this.selectedContract = null
            this.editMode = !this.editMode
            this.fetchContracts(this.lastSearchedParams)
            this.showWizard = false
        },
        closeWizard() {
            this.showWizard = false
            this.editMode = false
            this.selectedContract = null
        },
        addContract: function () {
            this.showWizard = true
            this.editMode = true
            this.selectedContract = this.newContract()
        },
        addDays(days) {
            let now = new Date(this.calendarStartDate)
            now.setDate(now.getDate()-days)
            now.setHours(12)
            this.calendarStartDate = now.toISOString().slice(0, 10);
        },
        goToToday() {
            // Reset the calendar view back to the current day
            this.calendarStartDate = this.getCurrentDay();
        },
        showContract(contract) {
            if (!this.disableContractActions) {
                this.editMode = !this.editMode
                if (contract && contract.id) {
                    this.fetchContract(contract.id)
                }
            }
        },
        fetchContract: function(id) {
            this.loading = true
            let params = {}
            params.id = id
            this.restFetchParams(this.contractUrl, params, this.handleContract, this.fail)
        },
        handleContract: function (response) {
            let fetchedContract = response ? response.data[0] : {}
            this.selectedContract = this.jsonToItem(fetchedContract)
            this.loading = false
        },
        addOrder: function (contract) {
            this.selectedOrder = this.createEditorOrder()
            this.selectedOrder.contract = contract ? contract.id : null
            this.selectedOrder.contract_name = contract ? contract.name : null
            this.selectedOrder.customer = contract ? contract.customer.id : null
            this.selectedOrder.customer_name = contract ? contract.customer.name : null
            this.selectedOrder.contractor = contract ? contract.contractor.id : null
            this.selectedOrder.contractor_name = contract ? contract.contractor.name : null
        },
        showOrder(order) {
            if (order && order.id) {
                this.fetchOrder(order.id)
            }
        },
        fetchOrder: function (id) {
            this.loading = true
            this.restFetch(this.orderUrl + "/" + id, this.handleOrders, this.fail)
        },
        handleOrders: function (response) {
            let fetchedOrder = response ? response.data : {}
            this.selectedOrder = this.createEditorOrder(fetchedOrder)
            this.loading = false
        },
        addAssignment: function (contract) {
            let newAssignment = {
                contract: contract,
                work_order: contract.work_order && contract.work_order.id ? contract.work_order : null
            }
            this.selectedAssignment = this.newWorkAssignment(newAssignment)
        },
        showAssignment(assignment) {
            if (assignment) {
                this.selectedAssignment = assignment
            }
        },
        checkActiveOrder: function(active){
            this.activeOrderEditor = active
        },
        setCustomer: function (customer) {
            this.selectedContract.customer = customer
            this.editMode = (this.selectedContract.customer && this.selectedContract.customer.id && this.selectedContract.contractor && this.selectedContract.contractor.id)
            this.showWizard = !this.editMode
        },
        setContractor: function (contractor) {
            this.selectedContract.contractor = contractor
            this.editMode = (this.selectedContract.customer && this.selectedContract.customer.id && this.selectedContract.contractor && this.selectedContract.contractor.id)
            this.showWizard = !this.editMode
        },
        closeOrder() {
            if (this.selectedOrder !== null) {
                this.refresh()
                this.$emit('orderUpdated')
                this.selectedOrder = null
            }
        },
        closeAssignment() {
            if (this.selectedAssignment != null) {
                let index = this.contracts.findIndex(item => item.id === this.selectedAssignment.contract.id)
                if (index >= 0){
                    this.$refs.contracts[index].fetchAssignments()
                }
                this.selectedAssignment = null
            }
        },
        closeAssignmentEditor() {
            // Plain cancel - no changes were made, so just close the editor
            // without re-fetching (avoids the assignment briefly/permanently
            // disappearing from the calendar due to an unrelated refetch).
            this.selectedAssignment = null
        },
        refresh() {
            if (this.contracts.length > 0 && this.selectedOrder) {
                let index = this.contracts.findIndex(item => item.id === this.selectedOrder.contract)
                if (index >= 0){
                    this.$refs.contracts[index].refreshContract(this.contracts[index])
                }
            }
        },
        refreshDutyOfficer() {
            if (this.contracts.length === 1) {
                this.$refs.contracts[0].fetchDutyOfficerSchedules()
            }
        }
    }
}
</script>
<style scoped>

#fa-arrow-date-right, #fa-arrow-date-left {
    background: #ffffff;
    border-top: 1px #BCBCBC solid;
    border-right: 1px #BCBCBC solid;
    color: #3B3933;
    margin: .1em;
    padding: .3em;
    font-size: .8em;
    user-select: none;
    min-height: .3em;
}
#fa-arrow-date-right:hover, #fa-arrow-date-left:hover {
    font-weight: bold;
}
@media (min-width: 768px) {
    #filter {
        top: -20px;
    }
}

.calendar-header-wrapper {
    position: sticky;
    top: 0;
    z-index: 20;
    background: white;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
}

.calendar-row-contract {
    display:block;
    width:auto;
    padding-bottom: .5em
}


</style>
